import type { DrillLegacyRecord, DrillRecordListItem } from './record-types.js';
import { filterRecords, type RecordFilter } from './record-view.js';

/**
 * Records arşivi (ortağın belgesi §8; ADR 0128 Faz 5 Ek): tarih aralığı, tür, durum ve aramayla
 * süzülen tatbikat kayıtları ile eski ISM tatbikat formlarının "Legacy" satırları. Legacy satırı
 * taşınmaz, seçilemez ve toplu dışa aktarıma girmez; tıklanınca asıl form kaydı açılır.
 */

export interface ArchiveFilter {
  status: RecordFilter;
  from: string | null;
  to: string | null;
  categoryId: string | null;
  search: string;
}

export type ArchiveRow =
  | { kind: 'record'; day: string; record: DrillRecordListItem }
  | { kind: 'legacy'; day: string; legacy: DrillLegacyRecord };

/** Bir dışa aktarımda en çok bu kadar kayıt (PDF istemcide üretilir). */
export const ARCHIVE_EXPORT_MAX = 50;

const norm = (value: string | null | undefined) => (value ?? '').toLocaleLowerCase('tr');

function inRange(day: string, filter: ArchiveFilter): boolean {
  return (!filter.from || day >= filter.from) && (!filter.to || day <= filter.to);
}

/** Süzgeç: kayıtlar mevcut sırayla (süren ve inceleme bekleyen önce), ardından Legacy satırları. */
export function archiveRows(
  records: readonly DrillRecordListItem[],
  legacy: readonly DrillLegacyRecord[],
  filter: ArchiveFilter,
): ArchiveRow[] {
  const words = norm(filter.search).split(/\s+/).filter(Boolean);
  const matches = (haystack: (string | null | undefined)[]) => {
    const text = haystack.map(norm).join(' ');
    return words.every((w) => text.includes(w));
  };
  const rows: ArchiveRow[] = filterRecords(records, filter.status)
    .filter(
      (r) =>
        inRange(r.drillDate, filter) &&
        (!filter.categoryId || r.categoryId === filter.categoryId) &&
        matches([
          r.recordNo,
          r.templateCode,
          r.templateName,
          r.categoryName,
          r.responsibleName,
          r.vesselName,
        ]),
    )
    .map((record) => ({ kind: 'record', day: record.drillDate, record }));
  // Legacy: türü bilinmez (tür süzgecinde görünmez); durum süzgecinde yalnız "Approved" eşleşir
  if (!filter.categoryId && (filter.status === 'all' || filter.status === 'approved')) {
    for (const item of [...legacy].sort((a, b) => b.recordDay.localeCompare(a.recordDay))) {
      if (filter.status === 'approved' && item.status !== 'approved') continue;
      if (!inRange(item.recordDay, filter)) continue;
      if (!matches([item.recordNo, item.docNo, item.documentTitle, item.title, item.vesselName])) {
        continue;
      }
      rows.push({ kind: 'legacy', day: item.recordDay, legacy: item });
    }
  }
  return rows;
}

/** Seçimi görünen kayıtlarla sınırlar (Legacy seçilmez). */
export function selectableIds(rows: readonly ArchiveRow[]): string[] {
  return rows.flatMap((row) => (row.kind === 'record' ? [row.record.id] : []));
}
